import parseJwt from "src/lib/parseJwt";

export type ExchangeTokenResult =
  | { status: "invalid" }
  | { status: "missing" }
  | { status: "ok"; token: string; userId?: string; tokenType: string };

/**
 * Decodes the one-time token from the handover URL.
 *
 * Pure on purpose: this used to live inside a `useMemo` that called `setError`
 * in its body — a state update during render, which React may re-run and which
 * made the surrounding effects hard to guard.
 */
export const parseExchangeToken = (
  token: string | null
): ExchangeTokenResult => {
  if (!token) {
    return { status: "missing" };
  }

  try {
    const decoded = parseJwt(token);
    return {
      status: "ok",
      token,
      userId: decoded.sub,
      tokenType: decoded.type
    };
  } catch {
    return { status: "invalid" };
  }
};

/**
 * True for the statuses the exchange endpoint returns when a single-use token
 * has already been consumed or has expired. That is a normal end to a handover,
 * not an application fault, so it is reported as an expected error rather than
 * captured as a Sentry issue.
 */
export const isSpentTokenError = (error: unknown): boolean => {
  if (!error || typeof error !== "object") {
    return false;
  }

  const status = (error as { status?: unknown }).status;
  return status === 400 || status === 401;
};
